export const PROFIT_BASE = {
  leads: 100,
  crPct: 30,
  price: 15000,
  q: 1.5,
  marginPct: 40,
};

export const PROFIT_LEVERS = [
  {
    id: 'leads',
    label: 'Лиды — заявки в месяц',
    hint: 'Охват рекламы, входящий поток',
    min: 10,
    max: 1000,
    step: 10,
    defaultValue: PROFIT_BASE.leads,
    format: 'number',
    color: '#185fa5',
    track: '#e6f1fb',
    icon: 'leads',
  },
  {
    id: 'crPct',
    label: 'Конверсия продаж',
    hint: 'Заявка → оплата, скрипты, скорость',
    min: 5,
    max: 80,
    step: 5,
    defaultValue: PROFIT_BASE.crPct,
    format: 'percent',
    color: '#2a7a52',
    track: '#edf7f2',
    icon: 'cr',
  },
  {
    id: 'price',
    label: 'Средний чек',
    hint: 'Цена, апсейл, глубина покупки',
    min: 1000,
    max: 300000,
    step: 1000,
    defaultValue: PROFIT_BASE.price,
    format: 'money',
    color: '#c84b2f',
    track: '#fdf0ed',
    icon: 'price',
  },
  {
    id: 'q',
    label: 'Повторные покупки (LTV)',
    hint: 'Сколько раз клиент покупает за год',
    min: 1,
    max: 12,
    step: 0.5,
    defaultValue: PROFIT_BASE.q,
    format: 'multiplier',
    color: '#5340b0',
    track: '#eeedfe',
    icon: 'q',
  },
  {
    id: 'marginPct',
    label: 'Маржинальность',
    hint: 'Доля прибыли в выручке после расходов',
    min: 5,
    max: 90,
    step: 5,
    defaultValue: PROFIT_BASE.marginPct,
    format: 'percent',
    color: '#993556',
    track: '#fbeaf0',
    icon: 'margin',
  },
];

export const DEFAULT_PROFIT_INPUTS = PROFIT_LEVERS.reduce((acc, lever) => {
  acc[lever.id] = lever.defaultValue;
  return acc;
}, {});

export const formatMoney = (value, locale = 'ru-RU') =>
  `${Math.round(value).toLocaleString(locale)} ₸`;

export const formatNumber = (value, locale = 'ru-RU') => Math.round(value).toLocaleString(locale);

export const formatPercent = (value) => `${Math.round(value)}%`;

export const formatMultiplier = (value) => `${value}×`;

export const getSliderFillPercent = (value, min, max) => {
  if (max <= min) {
    return 0;
  }

  return ((value - min) / (max - min)) * 100;
};

export const calcProfit = ({ leads, crPct, price, q, marginPct }) =>
  leads * (crPct / 100) * price * q * (marginPct / 100);

export const calculateProfitMetrics = (inputs) => {
  const { leads, crPct, price, q, marginPct } = inputs;
  const clients = Math.round(leads * (crPct / 100));
  const ltv = price * q;
  const revenue = clients * ltv;
  const profit = revenue * (marginPct / 100);
  const ppc = ltv * (marginPct / 100);
  const baseProfit = calcProfit(PROFIT_BASE);

  return {
    clients,
    ltv,
    revenue,
    profit,
    ppc,
    baseProfit,
  };
};

export const calculateLeverImpacts = (inputs) => {
  const { leads, crPct, price, q, marginPct } = inputs;
  const profit = calcProfit(inputs);
  const delta = 0.01;
  const impacts = {
    leads: Math.abs(calcProfit({ leads: leads * (1 + delta), crPct, price, q, marginPct }) - profit),
    crPct: Math.abs(calcProfit({ leads, crPct: crPct * (1 + delta), price, q, marginPct }) - profit),
    price: Math.abs(calcProfit({ leads, crPct, price: price * (1 + delta), q, marginPct }) - profit),
    q: Math.abs(calcProfit({ leads, crPct, price, q: q * (1 + delta), marginPct }) - profit),
    marginPct: Math.abs(
      calcProfit({ leads, crPct, price, q, marginPct: marginPct * (1 + delta) }) - profit,
    ),
  };
  const maxImpact = Math.max(...Object.values(impacts));

  return Object.fromEntries(
    Object.entries(impacts).map(([key, value]) => [
      key,
      maxImpact > 0 ? Math.round((value / maxImpact) * 100) : 0,
    ]),
  );
};

export const getScenarioTargets = (inputs) => {
  const { leads, crPct, price, q, marginPct } = inputs;

  return {
    leads: Math.round(leads + Math.max(20, Math.round(leads * 0.3))),
    crPct: Math.min(Math.round(crPct / 5) * 5 + 10, 80),
    price: Math.round((price * 1.15) / 1000) * 1000,
    q: Math.min(Math.round((q + 0.5) * 2) / 2, 12),
    marginPct: Math.min(Math.round(marginPct / 5) * 5 + 10, 90),
  };
};

export const calculateScenarioDeltas = (inputs) => {
  const { leads, crPct, price, q, marginPct } = inputs;
  const profit = calcProfit(inputs);
  const targets = getScenarioTargets(inputs);

  return {
    targets,
    deltas: {
      leads: calcProfit({ leads: targets.leads, crPct, price, q, marginPct }) - profit,
      crPct: calcProfit({ leads, crPct: targets.crPct, price, q, marginPct }) - profit,
      price: calcProfit({ leads, crPct, price: targets.price, q, marginPct }) - profit,
      q: calcProfit({ leads, crPct, price, q: targets.q, marginPct }) - profit,
      marginPct: calcProfit({ leads, crPct, price, q, marginPct: targets.marginPct }) - profit,
    },
  };
};

export const getInsightState = ({ profit, crPct, q, scenarioDeltas }) => {
  const bestKey = Object.entries(scenarioDeltas).sort((a, b) => b[1] - a[1])[0][0];

  if (profit <= 0) {
    return { variant: 'bad', message: 'Бизнес убыточен при текущих параметрах: расходы <b>{totalCost}</b> превышают выручку <b>{revenue}</b>. Нужно снизить себестоимость или поднять цену.' };
  }

  if (crPct < 15) {
    return {
      variant: 'warn',
      message: 'Конверсия продаж <b>{crPct}%</b> — слабое место. Из 10 заявок покупает только <b>{buyers}</b>. Скрипт, скорость ответа и квалификация лида дадут больший прирост, чем рост бюджета.',
      vars: { crPct, buyers: Math.round(crPct / 10) },
    };
  }

  if (q < 1.5) {
    return { variant: 'warn', message: 'Клиент покупает <b>{q}×</b> и не возвращается. Рассылка, программа лояльности, звонок через месяц — даже небольшая работа по удержанию удвоит LTV без роста бюджета.', vars: { q } };
  }

  return { variant: 'good', message: 'Главный рычаг сейчас — <b>{leverName}</b>. Нажмите на строку сценария — увидите как изменится прибыль.', vars: { bestLeverId: bestKey } };
};

export const getProfitDeltaState = ({ appliedScenario, profit, baseProfit, scenarioName }) => {
  if (!appliedScenario) {
    return null;
  }

  const delta = profit - baseProfit;

  if (delta > 0) {
    return {
      variant: 'up',
      message: '↑ +{delta} если поднять {scenarioName}',
      vars: { delta, scenarioName },
    };
  }

  if (delta < 0) {
    return {
      variant: 'down',
      message: '↓ {delta} к базовым цифрам',
      vars: { delta },
    };
  }

  return { variant: 'zero', message: 'без изменений' };
};
